/**
 * SourceGroupsApi — org-scoped source groups (/admin/source-groups, /admin/inputs/{id}/groups).
 *
 * Methods-only mixin merged onto AdminAPIClient via applyMixins in client.ts.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import type {
  CreateSourceGroupRequest,
  SourceGroup,
  SourceGroupActionResponse,
  SourceGroupDetail,
  SourceGroupListResponse,
  SourceGroupMembershipListResponse,
  UpdateSourceGroupRequest,
} from '@/types/source-groups';
import { API_ENDPOINTS } from '@/utils/env';

import { BaseApiClient } from './base';

export class SourceGroupsApi extends BaseApiClient {
  async listSourceGroups(): Promise<SourceGroupListResponse> {
    return this.fetchJSON<SourceGroupListResponse>(API_ENDPOINTS.sourceGroups, { transformKeys: false });
  }

  async getSourceGroup(groupId: string): Promise<SourceGroupDetail> {
    return this.fetchJSON<SourceGroupDetail>(API_ENDPOINTS.sourceGroup(groupId), { transformKeys: false });
  }

  async createSourceGroup(request: CreateSourceGroupRequest): Promise<SourceGroup> {
    return this.fetchJSON<SourceGroup>(API_ENDPOINTS.sourceGroups, { method: 'POST', body: request, transformKeys: false });
  }

  async updateSourceGroup(groupId: string, request: UpdateSourceGroupRequest): Promise<SourceGroup> {
    return this.fetchJSON<SourceGroup>(API_ENDPOINTS.sourceGroup(groupId), { method: 'PUT', body: request, transformKeys: false });
  }

  async deleteSourceGroup(groupId: string): Promise<SourceGroupActionResponse> {
    return this.fetchJSON<SourceGroupActionResponse>(API_ENDPOINTS.sourceGroup(groupId), { method: 'DELETE', transformKeys: false });
  }

  async removeSourceGroupMember(groupId: string, inputSourceId: string): Promise<SourceGroupActionResponse> {
    return this.fetchJSON<SourceGroupActionResponse>(API_ENDPOINTS.sourceGroupMember(groupId, inputSourceId), {
      method: 'DELETE',
      transformKeys: false,
    });
  }

  async getSourceGroupsForSource(sourceId: string): Promise<SourceGroupMembershipListResponse> {
    return this.fetchJSON<SourceGroupMembershipListResponse>(API_ENDPOINTS.inputSourceGroups(sourceId), { transformKeys: false });
  }

  async setSourceGroupsForSource(sourceId: string, groupIds: string[]): Promise<SourceGroupMembershipListResponse> {
    return this.fetchJSON<SourceGroupMembershipListResponse>(API_ENDPOINTS.inputSourceGroups(sourceId), {
      method: 'PUT',
      body: { group_ids: groupIds },
      transformKeys: false,
    });
  }
}
